import { useState, useEffect, useMemo } from 'react';
import { getTelegramWebApp, haptic, initTelegramWebApp } from './lib/telegram';
import { api, Item, User, UserProfile, ParsedUserProfile, CandidateProfile, CreateWorkData, MarketProfile, UserPurchase, PortfolioItem } from './lib/api';
import RegistrationWizard from './features/profile/RegistrationWizard';
import EditProfileWizard from './features/profile/EditProfileWizard';
import { ProfileView } from './components/profile/ProfileView';
import { PrivacySettingsView } from './components/profile/PrivacySettingsView';
import { VerificationView } from './components/profile/VerificationView';
import { isDevModeEnabled } from './lib/devMode';
import { devUser } from './lib/mocks';
import { t } from './lib/i18n';
import { BottomNav } from './components/BottomNav';
import { TopTabs } from './components/TopTabs';
import { SwipeCardProfile } from './components/SwipeCardProfile';
import { PortfolioMiniCard } from './components/profile/PortfolioMiniCard';
import { FilterChip } from './components/FilterChip';
import { SearchBar } from './components/SearchBar';
import { FilterDropdown, FilterOptions } from './components/FilterDropdown';
import { SortDropdown } from './components/SortDropdown';
import { HeroCarousel } from './components/HeroCarousel';
import { ItemDetailModal } from './components/ItemDetailModal';
import { PortfolioSwipeCardModal } from './components/profile/PortfolioSwipeCardModal';
import { UserProfileModal } from './components/profile/UserProfileModal';
import { ChatsView } from './components/ChatsView';
import { ChatConversationView } from './components/ChatConversationView';
import { BoostPromoView } from './components/BoostPromoView';
import { UploadWorkView } from './components/UploadWorkView';
import { Sparkles, Loader2 } from 'lucide-react';
import { PurchasesList } from './components/PurchasesList';

type SwipeCandidateItem = Item & { candidate: CandidateProfile; candidateCategoryLabel: string };

export default function App() {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeBottomTab, setActiveBottomTab] = useState('home');
  const [activeTopTab, setActiveTopTab] = useState<'Marketplace' | 'Swipes'>('Swipes');
  const [likedItems, setLikedItems] = useState<Set<string>>(new Set());
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [swipeItems, setSwipeItems] = useState<SwipeCandidateItem[]>([]);
  const [swipesRemaining, setSwipesRemaining] = useState<number | null>(null);
  const [marketplaceItems, setMarketplaceItems] = useState<Item[]>([]);
  const [swipeIndex, setSwipeIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [showItemInfo, setShowItemInfo] = useState(false);
  const [selectedArtistProfile, setSelectedArtistProfile] = useState<ParsedUserProfile | null>(null);
  const [showSearchPage, setShowSearchPage] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  
  // Marketplace enhancements
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({});
  const [sortOption, setSortOption] = useState<'popular' | 'trending' | 'newest'>('popular');
  const [searchResults, setSearchResults] = useState<{
    data: Item[];
    corrected_query?: string;
    original_query: string;
    related_queries: string[];
    similar_items: Item[];
  } | null>(null);
  const [forYouItems, setForYouItems] = useState<Item[]>([]);
  const [popularItems, setPopularItems] = useState<Item[]>([]);
  const [trendingItems, setTrendingItems] = useState<Item[]>([]);
  const [newItems, setNewItems] = useState<Item[]>([]);
  const [freeItems, setFreeItems] = useState<Item[]>([]);
  
  // Creator lists by role
  const [popularArtists, setPopularArtists] = useState<User[]>([]);
  const [popularBeatmakers, setPopularBeatmakers] = useState<User[]>([]);
  const [popularDesigners, setPopularDesigners] = useState<User[]>([]);
  const [popularProducers, setPopularProducers] = useState<User[]>([]);
  const [popularSoundEngineers, setPopularSoundEngineers] = useState<User[]>([]);
  
  // Profile state
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [referralLink, setReferralLink] = useState<string>('');
  
  // Chat state
  const [selectedChat, setSelectedChat] = useState<{ id: string; name: string; photo?: string } | null>(null);
  
  // Boost promo state
  const [showBoostPromo, setShowBoostPromo] = useState(false);
  
  // Upload state
  const [showUploadView, setShowUploadView] = useState(false);

  // Purchases state
  const [purchases, setPurchases] = useState<UserPurchase[]>([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  
  const tg = getTelegramWebApp();
  const translations = t();

  const buildFallbackAvatar = (name?: string | null) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Splyt')}&size=512&background=1a1f2e&color=00FF88`;

  const mapCandidateToSwipeItem = (candidate: CandidateProfile): SwipeCandidateItem => {
    const displayName = candidate.name || (candidate.username ? `@${candidate.username}` : 'Творець Splyt');
    const coverUrl = candidate.photo_url || buildFallbackAvatar(displayName);
    const categoryLabel = candidate.roles_display?.[0] || candidate.roles?.[0] || 'creator';
    return {
      id: `candidate-${candidate.telegram_id}`,
      creator_id: candidate.telegram_id,
      category: 'service',
      title: displayName,
      description: candidate.bio || '',
      price_uah: 0,
      tags: [...(candidate.roles_display ?? candidate.roles ?? []), ...(candidate.styles || [])],
      bpm: undefined,
      key: undefined,
      formats: [],
      preview_url: '',
      media_url: '',
      cover_url: coverUrl,
      is_exclusive: false,
      status: 'active',
      created_at: candidate.last_activity_at || new Date().toISOString(),
      creator: {
        id: candidate.telegram_id,
        name: displayName,
        username: candidate.username,
        avatar: candidate.photo_url || undefined,
      },
      stats: {
        likes: candidate.total_likes_received ?? 0,
        plays: 0,
      },
      candidate,
      candidateCategoryLabel: categoryLabel,
    };
  };

  const candidateToParsedProfile = (candidate: CandidateProfile): ParsedUserProfile => ({
    telegram_id: candidate.telegram_id,
    username: candidate.username,
    name: candidate.name,
    photo_file_id: candidate.photo_file_id ?? null,
    roles: candidate.roles || [],
    city: candidate.city || '',
    styles: (candidate.styles || []).join(', '),
    bio: candidate.bio || '',
    portfolios: candidate.portfolios || [],
    is_boosted: candidate.is_boosted,
    boost_expires_at: null,
    referral_count: undefined,
  });

const parseRolesString = (roles?: string | string[] | null): string[] => {
  if (!roles) return [];
  if (Array.isArray(roles)) {
    return roles.map((role) => String(role).trim()).filter(Boolean);
  }
  return String(roles)
    .split(',')
    .map((role) => role.trim())
    .filter(Boolean);
};

const marketProfileToParsed = (profile: MarketProfile): ParsedUserProfile => {
  const { stats, roles, ...rest } = profile;
  return {
    ...rest,
    roles: parseRolesString(roles),
    stats,
  };
};

  // Initialize app
  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    let telegramId: number | undefined;
    try {
      setLoading(true);
      
      // Initialize Telegram WebApp environment when available
      try { initTelegramWebApp(); } catch {}

      // Authenticate with backend (mocked when Developer Mode is enabled)
      try {
        const authResponse = await api.auth.init();
        setAuthUser(authResponse.user);
        telegramId = authResponse.user.telegram_id;
        console.log('✅ App authenticated successfully', authResponse.user);
      } catch (authError) {
        console.error('❌ Failed to authenticate:', authError);
        if (isDevModeEnabled()) {
          // Seed a local dev user so the UI can be explored without Telegram
          setAuthUser(devUser);
          telegramId = devUser.telegram_id;
        }
      }
      
      // Fetch profile and decide onboarding
      try {
        const p = await api.profile.get();
        setUserProfile(p);
        const rolesMissing = !p?.roles || String(p.roles).trim().length === 0;
        setShowOnboarding(rolesMissing);
        if (!telegramId) {
          telegramId = p.telegram_id;
        }
        
        // Fetch referral info
        try {
          const referral = await api.referral.get();
          setReferralLink(referral.link);
        } catch (e) {
          console.warn('Failed to fetch referral info:', e);
        }
      } catch (e) {
        // If profile endpoint not ready, skip onboarding to avoid blocking
        console.warn('Profile fetch failed or not available yet:', e);
      }
      
      // Load initial content
      await loadSwipeItems(telegramId);
      await loadMarketplaceItems();
      
      console.log('✅ App initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize app:', error);
      // Avoid blocking alerts to preserve UX
    } finally {
      setLoading(false);
    }
  };

  const loadSwipeItems = async (telegramId?: number) => {
    const effectiveId = telegramId ?? authUser?.telegram_id ?? userProfile?.telegram_id;

    if (!effectiveId) {
      setSwipeItems([]);
      setSwipeIndex(0);
      setSwipesRemaining(null);
      return;
    }

    try {
      const response = await api.swipes.getForYou(effectiveId);
      const items = Array.isArray(response.data)
        ? response.data.map(mapCandidateToSwipeItem)
        : [];
      setSwipeItems(items);
      setSwipeIndex(0);
      setSwipesRemaining(response.swipes_remaining ?? null);
    } catch (error) {
      console.error('Failed to load swipe items:', error);
      setSwipeItems([]);
      setSwipeIndex(0);
      setSwipesRemaining(null);
    }
  };

  const categoryMap: Record<string, Item['category'] | 'all'> = {
    all: 'all',
    beats: 'beat',
    covers: 'cover',
    features: 'feature',
    mixing: 'mix',
    kits: 'kit',
    services: 'service',
  };

  const loadMarketplaceItems = async () => {
    try {
      setItemsLoading(true);
      const params: {
        category?: string;
        sort?: string;
        filters?: Record<string, unknown>;
      } = {};
      
      if (selectedCategory !== 'all') {
        const mapped = categoryMap[selectedCategory] as string;
        params.category = mapped;
      }
      
      // Don't include searchQuery here - that's handled by handleSearch
      // Only apply filters (not search)
      if (filters.genre) {
        params.filters = { ...params.filters, genre: filters.genre };
      }
      if (filters.bpmMin || filters.bpmMax) {
        params.filters = { 
          ...params.filters, 
          bpm_min: filters.bpmMin,
          bpm_max: filters.bpmMax,
        };
      }
      if (filters.priceType && filters.priceType !== 'all') {
        params.filters = { 
          ...params.filters, 
          price_type: filters.priceType,
        };
      }
      
      const response = await api.catalog.getItems(params);
      
      setMarketplaceItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load marketplace items:', error);
      setMarketplaceItems([]);
    } finally {
      setItemsLoading(false);
    }
  };


  const loadCreators = async () => {
    try {
      const [artists, beatmakers, designers, producers, soundEngineers] = await Promise.all([
        api.creators.getByRole('performer', 10),
        api.creators.getByRole('beatmaker', 10),
        api.creators.getByRole('designer', 10),
        api.creators.getByRole('producer', 10),
        api.creators.getByRole('producer', 10), // Sound engineers are also producers
      ]);

      setPopularArtists(artists.data);
      setPopularBeatmakers(beatmakers.data);
      setPopularDesigners(designers.data);
      setPopularProducers(producers.data);
      setPopularSoundEngineers(soundEngineers.data);
    } catch (error) {
      console.error('Failed to load creators:', error);
      // Silently fail - creators are optional
    }
  };

  const loadForYouItems = async () => {
    try {
      const response = await api.recommendations.get(20);
      setForYouItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load for you items:', error);
      setForYouItems([]);
    }
  };

  const loadSubcategoryItems = async () => {
    try {
      // Map UI category keys (e.g., 'beats') to API categories (e.g., 'beat')
      const mapped = categoryMap[selectedCategory];
      const categoryParam = mapped && mapped !== 'all' ? mapped : undefined;
      const baseFilters = categoryParam ? { category: categoryParam } : undefined;

      const [popularResponse, trendingResponse, newResponse, freeResponse] = await Promise.all([
        api.catalog.getItems({
          category: categoryParam,
          sort: 'popular',
          filters: baseFilters,
        }),
        api.catalog.getItems({
          category: categoryParam,
          sort: 'trending',
          filters: baseFilters,
        }),
        api.catalog.getItems({
          category: categoryParam,
          sort: sortOption,
          filters: baseFilters,
        }),
        api.catalog.getItems({
          category: categoryParam,
          sort: 'newest',
          filters: {
            ...(baseFilters ?? {}),
            price_type: 'free',
          },
        }),
      ]);

      setPopularItems(popularResponse.data.slice(0, 10));
      setTrendingItems(trendingResponse.data.slice(0, 10));
      setNewItems(newResponse.data.slice(0, 10));
      setFreeItems(freeResponse.data.slice(0, 10));
    } catch (error) {
      console.error('Failed to load subcategory items:', error);
      // Show nothing if API fails to ensure only database data is displayed
      setPopularItems([]);
      setTrendingItems([]);
      setNewItems([]);
      setFreeItems([]);
    }
  };

  const loadPurchases = async () => {
    try {
      setPurchasesLoading(true);
      const list = await api.user.getPurchases();
      setPurchases(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to load purchases:', error);
      setPurchases([]);
    } finally {
      setPurchasesLoading(false);
    }
  };

  const toggleLike = async (itemId: string) => {
    try {
      await api.user.favorite(itemId);
    } catch (error) {
      // Silently fail - keep local state in sync
      console.log('Failed to toggle like on API, using local state:', error);
    }
    
    setLikedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
    
    haptic.light();
  };

  const processSwipe = async (direction: 'left' | 'right') => {
    if (swipeIndex >= swipeItems.length) return;

    const currentItem = swipeItems[swipeIndex];
    const candidate = currentItem?.candidate;
    if (!candidate) {
      setSwipeIndex((prev) => prev + 1);
      return;
    }

    const telegramId = authUser?.telegram_id ?? userProfile?.telegram_id;
    if (!telegramId) {
      console.warn('Cannot process swipe without telegram_id');
      setSwipeIndex((prev) => prev + 1);
      return;
    }

    let shouldAdvance = false;

    try {
      if (direction === 'right') {
        const response = await api.swipes.like(telegramId, candidate.telegram_id);
        setSwipesRemaining(response.swipes_remaining ?? null);
        shouldAdvance = true;
        if (response.is_match) {
          tg?.showAlert('🔥 Це Match! Перевір повідомлення від бота.');
          haptic.success();
        } else {
          haptic.success();
        }
      } else {
        const response = await api.swipes.skip(telegramId, candidate.telegram_id);
        setSwipesRemaining(response.swipes_remaining ?? null);
        shouldAdvance = true;
        haptic.light();
      }
    } catch (error: any) {
      console.error('Failed to process swipe:', error);
      const status = error?.response?.status;
      if (status === 429) {
        setSwipesRemaining(0);
        setSwipeIndex(swipeItems.length);
        tg?.showAlert('Ви використали всі свайпи на сьогодні. Повертайся завтра!');
      } else {
        tg?.showAlert('Не вдалося обробити свайп. Спробуй ще раз.');
      }
      haptic.error();
    } finally {
      if (shouldAdvance) {
        setSwipeIndex((prev) => prev + 1);
      }
    }
  };

  const handleSwipe = (direction: 'left' | 'right') => {
    void processSwipe(direction);
  };

  const handleBottomTabChange = (tab: string) => {
    setActiveBottomTab(tab);
    haptic.selection();
    setShowSearchPage(false);
    // Reset selected chat when switching away from chats tab
    if (tab !== 'chats') {
      setSelectedChat(null);
    }
    // Show upload view when add tab is selected
    if (tab === 'add') {
      setShowUploadView(true);
    } else {
      setShowUploadView(false);
    }
  };

  const handleTopTabChange = (tab: 'Marketplace' | 'Swipes') => {
    setActiveTopTab(tab);
    haptic.selection();
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    
    // If query is empty, clear search results and reload normal items
    if (!query.trim()) {
      setSearchResults(null);
      loadMarketplaceItems();
      return;
    }
    
    // Only use enhanced search API if query is at least 2 characters
    if (query.trim().length >= 2) {
      try {
        setItemsLoading(true);
        // Pass current filters and category to backend search for consistent behavior
        const searchFilters: Record<string, unknown> = {};
        const mapped = categoryMap[selectedCategory];
        if (mapped && mapped !== 'all') searchFilters.category = mapped;
        if (filters.genre) searchFilters.genre = filters.genre;
        if (filters.bpmMin !== undefined) searchFilters.bpm_min = filters.bpmMin;
        if (filters.bpmMax !== undefined) searchFilters.bpm_max = filters.bpmMax;
        if (filters.priceType && filters.priceType !== 'all') searchFilters.price_type = filters.priceType;
        const results = await api.search.query(query, searchFilters);
        setSearchResults(results);
        // If backend finds results, use them. Otherwise, load a broader list and
        // let client-side fuzzy matching surface near matches.
        if (Array.isArray(results.data) && results.data.length > 0) {
          setMarketplaceItems(results.data);
        } else {
          const fallbackParams: {
            category?: string;
            sort?: string;
            filters?: Record<string, unknown>;
          } = {};
          const fallbackFilters: Record<string, unknown> = {};
          const mapped = categoryMap[selectedCategory];
          if (mapped && mapped !== 'all') fallbackParams.category = mapped;
          fallbackParams.sort = 'popular';
          if (filters.genre) fallbackFilters.genre = filters.genre;
          if (filters.bpmMin !== undefined) fallbackFilters.bpm_min = filters.bpmMin;
          if (filters.bpmMax !== undefined) fallbackFilters.bpm_max = filters.bpmMax;
          if (filters.priceType && filters.priceType !== 'all') fallbackFilters.price_type = filters.priceType;
          if (Object.keys(fallbackFilters).length) fallbackParams.filters = fallbackFilters;
          const fallback = await api.catalog.getItems(fallbackParams);
          setMarketplaceItems(fallback.data || []);
        }
        setItemsLoading(false);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults(null);
        setItemsLoading(false);
        loadMarketplaceItems();
      }
    } else {
      // For single character queries, clear search results and show normal items
      setSearchResults(null);
      loadMarketplaceItems();
    }
  };

  const handleFilterApply = (newFilters: FilterOptions) => {
    setFilters(newFilters);
    loadMarketplaceItems();
  };

  const handleWrite = async (item: PortfolioItem) => {
    if (!item.creator_id) return;
    try {
      await api.chat.sendMessage(item.creator_id, `Check out this post: ${item.title}`);
      haptic.success();
      tg?.showAlert('Post forwarded to creator!');
    } catch (error) {
      console.error('Failed to send message:', error);
      haptic.error();
      tg?.showAlert('Failed to forward post.');
    }
  };

  const handleArtistClick = async (item: User | Item) => {
    haptic.light();
    // If it's an Item (has category), open item detail modal
    if ('category' in item) {
      setSelectedItem(item as Item);
    } else {
      const user = item as User;
      const telegramId = user.telegram_id || user.id;

      if (!telegramId) {
        console.warn('Cannot load marketplace profile without telegram_id', user);
        return;
      }

      try {
        const profile = await api.market.getProfile(telegramId);
        const parsedProfile = marketProfileToParsed(profile);
        setSelectedArtistProfile(parsedProfile);
      } catch (error) {
        console.error('Failed to load marketplace profile:', error);
        if (tg) {
          try {
            tg.showAlert?.('Не вдалося завантажити профіль автора');
          } catch (alertError) {
            console.warn('Telegram alert failed', alertError);
          }
        }
      }
    }
  };

  // Load marketplace items when category or filters change, but NOT when searchQuery changes
  useEffect(() => {
    // Only load if we're not in search mode
    if (!searchQuery.trim()) {
      loadMarketplaceItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, filters.genre, filters.bpmMin, filters.bpmMax, filters.priceType]);

  useEffect(() => {
    loadForYouItems();
    loadSubcategoryItems();
    loadCreators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]); // Remove marketplaceItems.length dependency to avoid loops

  // Load purchases when user opens Saved tab
  useEffect(() => {
    if (activeBottomTab === 'saved') {
      void loadPurchases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBottomTab]);

  // Client-side filtering helpers and memoized results must be defined
  // before any conditional returns to preserve hook order.
  const normalizedIncludes = (hay: string, needle: string) => {
    if (!hay || !needle) return false;
    return hay.toLowerCase().includes(needle.toLowerCase());
  };

  const levenshtein = (a: string, b: string): number => {
    const s = a.toLowerCase();
    const t = b.toLowerCase();
    const m = s.length;
    const n = t.length;
    if (m === 0) return n;
    if (n === 0) return m;
    const dp: number[] = new Array(n + 1);
    for (let j = 0; j <= n; j++) dp[j] = j;
    for (let i = 1; i <= m; i++) {
      let prev = i - 1;
      dp[0] = i;
      for (let j = 1; j <= n; j++) {
        const temp = dp[j];
        const cost = s[i - 1] === t[j - 1] ? 0 : 1;
        dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + cost);
        prev = temp;
      }
    }
    return dp[n];
  };

  const filteredItems = useMemo(() => {
    const items = Array.isArray(marketplaceItems) ? marketplaceItems : [];
    const mappedCategory = categoryMap[selectedCategory];

    const q = searchQuery.trim();

    return items.filter((item) => {
      if (mappedCategory !== 'all' && item.category !== mappedCategory) {
        return false;
      }

      if (filters.genre && item.tags && !item.tags.some((t) => t.toLowerCase() === String(filters.genre).toLowerCase())) {
        return false;
      }
      if (typeof filters.bpmMin === 'number' && typeof item.bpm === 'number' && item.bpm < filters.bpmMin) return false;
      if (typeof filters.bpmMax === 'number' && typeof item.bpm === 'number' && item.bpm > filters.bpmMax) return false;
      if (filters.priceType === 'free' && item.price_uah > 0) return false;
      if (filters.priceType === 'paid' && item.price_uah === 0) return false;

      if (!q) return true;
      const haystacks: string[] = [
        item.title,
        item.description,
        item.creator?.name || '',
        item.creator?.username || '',
        (item.tags || []).join(' '),
        item.category,
      ];

      const foundDirect = haystacks.some((h) => normalizedIncludes(h, q));
      if (foundDirect) return true;

      const threshold = q.length <= 4 ? 1 : 2;
      for (const h of haystacks) {
        const words = h.toLowerCase().split(/[\s_#/-]+/).filter(Boolean);
        for (const w of words) {
          if (levenshtein(w, q.toLowerCase()) <= threshold) return true;
        }
      }
      return false;
    });
  }, [marketplaceItems, selectedCategory, searchQuery, filters]);

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-splyt-gradient">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-splyt-green mx-auto mb-4" />
          <p className="text-white/70">{translations.common.loading}</p>
        </div>
      </div>
    );
  }

  // Edit profile wizard
  if (showEditProfile && userProfile) {
    return (
      <div className="min-h-screen bg-splyt-gradient">
        <EditProfileWizard 
          profile={userProfile}
          onComplete={async (profile: UserProfile) => {
            setUserProfile(profile);
            setShowEditProfile(false);
            haptic.success();
            await loadSwipeItems(profile.telegram_id);
            
            // Refresh referral info
            try {
              const referral = await api.referral.get();
              setReferralLink(referral.link);
            } catch (e) {
              console.warn('Failed to fetch referral info:', e);
            }
          }}
          onClose={() => {
            setShowEditProfile(false);
          }}
        />
      </div>
    );
  }

  if (showPrivacySettings) {
    return (
      <PrivacySettingsView onClose={() => setShowPrivacySettings(false)} />
    );
  }

  if (showVerification) {
    return (
      <VerificationView onClose={() => setShowVerification(false)} />
    );
  }

  // Onboarding wizard (registration flow) if roles are missing
  if (showOnboarding) {
    return (
      <div className="min-h-screen bg-splyt-gradient">
        <RegistrationWizard 
          onComplete={async (profile: UserProfile) => {
            setUserProfile(profile);
            setShowOnboarding(false);
            haptic.success();
            await loadSwipeItems(profile.telegram_id);
            
            // Refresh referral info
            try {
              const referral = await api.referral.get();
              setReferralLink(referral.link);
            } catch (e) {
              console.warn('Failed to fetch referral info:', e);
            }
          }}
          onClose={() => {
            if (userProfile) {
              setShowOnboarding(false);
            }
          }}
          initialProfile={userProfile || undefined}
        />
      </div>
    );
  }

  

  const savedItems = marketplaceItems.filter(item => likedItems.has(item.id));
  const currentSwipeItem = swipeItems[swipeIndex];

  // For You fallback logic:
  // If there are no personalized recommendations yet (e.g. no user activity),
  // show popular items in the "For You" section instead.
  const hasPersonalizedForYou = forYouItems.length > 0;
  const forYouDisplayItems = hasPersonalizedForYou ? forYouItems : popularItems;

  return (
    <div className="min-h-screen pb-16 relative bg-splyt-gradient">
      {/* Ambient background effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-splyt-green opacity-10 blur-[120px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-splyt-purple opacity-10 blur-[120px] rounded-full" />
      </div>
      
      {/* Header */}
      {(activeBottomTab === 'home' || showSearchPage) && (
        <div className="sticky top-0 z-40 px-2 pt-4 pb-2 glass backdrop-blur-2xl border-b border-white/10">
          {/* Top Tabs for Home */}
          {activeBottomTab === 'home' && !showSearchPage && (
            <TopTabs
              activeTab={activeTopTab}
              onTabChange={handleTopTabChange}
              tabs={['Marketplace', 'Swipes']}
              onSearchClick={() => {
                setShowSearchPage(true);
                haptic.light();
              }}
            />
          )}
          {/* Search Page Header */}
          {showSearchPage && (
            <div className="flex items-center gap-3">
              <button
                data-testid="back-button"
                onClick={() => {
                  setShowSearchPage(false);
                  haptic.light();
                }}
                className="w-10 h-10 rounded-xl flex items-center justify-center glass border border-white/10 hover:border-splyt-green/50 hover:bg-white/10 transition-all"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-xl font-bold text-white flex-1">Пошук</h2>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="px-4 relative z-10">
        {/* Search Page */}
        {showSearchPage && (
          <div className="pb-4">
            <div className="mb-6">
              <SearchBar
                onSearch={handleSearch}
                onFilterClick={() => setShowFilters(true)}
                placeholder="Шукати біти, артистів, кіти..."
                alwaysExpanded={true}
              />
            </div>
            
            {/* Category Filters */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {Object.entries(translations.categories).map(([key, label]) => (
                <FilterChip
                  key={key}
                  label={label}
                  isActive={selectedCategory === key}
                  onClick={() => {
                    setSelectedCategory(key);
                    haptic.selection();
                  }}
                />
              ))}
            </div>
            <SortDropdown onSortChange={setSortOption} currentSort={sortOption} />

            {/* Search Results */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-4 px-1">
                {searchQuery ? `Результати пошуку "${searchQuery}"` : 'Всі біти'}
              </h2>
              
              {/* Spell Correction / "Did You Mean" */}
              {searchResults?.corrected_query && searchResults.corrected_query !== searchResults.original_query && (
                <div className="mb-4 p-3 rounded-2xl glass border border-splyt-green/30 bg-splyt-green/10">
                  <p className="text-white/80 text-sm mb-1">Ви мали на увазі:</p>
                  <button
                    onClick={() => handleSearch(searchResults.corrected_query!)}
                    className="text-splyt-green font-semibold text-base hover:underline"
                  >
                    {searchResults.corrected_query}
                  </button>
                </div>
              )}
              
              {itemsLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="w-10 h-10 animate-spin text-splyt-green mb-3" />
                  <p className="text-white/60">{translations.common.loading}</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="space-y-6">
                  <div className="flex flex-col items-center justify-center py-20">
                    <Sparkles className="w-16 h-16 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50 text-center">
                      {searchQuery ? 'Нічого не знайдено' : 'Поки що тут порожньо'}
                    </p>
                  </div>
                  
                  {/* Related Searches / "People Also Search For" */}
                  {searchResults?.related_queries && searchResults.related_queries.length > 0 && (
                    <div className="px-1">
                      <h3 className="text-lg font-semibold text-white mb-3">Також шукали:</h3>
                      <div className="flex flex-wrap gap-2">
                        {searchResults.related_queries.map((relatedQuery, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSearch(relatedQuery)}
                            className="px-4 py-2 rounded-full glass border border-white/10 hover:border-splyt-green/50 hover:bg-white/10 text-white text-sm transition-all"
                          >
                            {relatedQuery}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {filteredItems.map((item) => (
                      <PortfolioMiniCard
                        key={item.id}
                        item={item}
                        onClick={() => setSelectedItem(item)}
                      />
                    ))}
                  </div>
                  
                  {/* Similar Items / Related Results */}
                  {searchResults?.similar_items && searchResults.similar_items.length > 0 && filteredItems.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold text-white mb-4 px-1">Схожі результати:</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {searchResults.similar_items.slice(0, 4).map((item) => (
                          <PortfolioMiniCard
                            key={item.id}
                            item={item}
                            onClick={() => setSelectedItem(item)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Related Searches / "People Also Search For" */}
                  {searchResults?.related_queries && searchResults.related_queries.length > 0 && (
                    <div className="mt-6 px-1">
                      <h3 className="text-lg font-semibold text-white mb-3">Також шукали:</h3>
                      <div className="flex flex-wrap gap-2">
                        {searchResults.related_queries.map((relatedQuery, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSearch(relatedQuery)}
                            className="px-4 py-2 rounded-full glass border border-white/10 hover:border-splyt-green/50 hover:bg-white/10 text-white text-sm transition-all"
                          >
                            {relatedQuery}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Home - Marketplace View */}
        {activeBottomTab === 'home' && activeTopTab === 'Marketplace' && !showSearchPage && (
          <div className="pb-4">
            {/* Category Filters (Product Type Nav) */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
              {Object.entries(translations.categories).map(([key, label]) => (
                <FilterChip
                  key={key}
                  label={label}
                  isActive={selectedCategory === key}
                  onClick={() => {
                    setSelectedCategory(key);
                    haptic.selection();
                  }}
                />
              ))}
            </div>

            {/* Dynamic Category-Based Sections */}
            
            {/* Всі (All) */}
            {selectedCategory === 'all' && (
              <>
                {/* For You Items (fallback to popular when user has no activity yet) */}
                {forYouDisplayItems.length > 0 && (
                  <HeroCarousel
                    title="Для тебе"
                    items={forYouDisplayItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={(sortType) => {
                      if (sortType) {
                        setSortOption(sortType);
                      }
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}

                {/* Popular Items (only show as separate section when For You is personalized) */}
                {hasPersonalizedForYou && popularItems.length > 0 && (
                  <HeroCarousel
                    title="Популярні"
                    items={popularItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    sortType="popular"
                    onShowMore={(sortType) => {
                      if (sortType) {
                        setSortOption(sortType);
                      }
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}

                {/* Popular Artists */}
                {popularArtists.length > 0 && (
                  <HeroCarousel
                    title="Популярні артисти"
                    items={popularArtists}
                    onItemClick={handleArtistClick}
                    type="artists"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}

                {/* Trending */}
                {trendingItems.length > 0 && (
                  <HeroCarousel
                    title="Трендові"
                    items={trendingItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    sortType="trending"
                    onShowMore={(sortType) => {
                      if (sortType) {
                        setSortOption(sortType);
                      }
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}

                {/* New */}
                {newItems.length > 0 && (
                  <HeroCarousel
                    title="Нові"
                    items={newItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    sortType="newest"
                    onShowMore={(sortType) => {
                      if (sortType) {
                        setSortOption(sortType);
                      }
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}

                {/* Free */}
                {freeItems.length > 0 && (
                  <HeroCarousel
                    title="Безкоштовні"
                    items={freeItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
              </>
            )}

            {/* Біти (Beats) */}
            {selectedCategory === 'beats' && (
              <>
                {popularItems.length > 0 && (
                  <HeroCarousel
                    title="Популярні біти"
                    items={popularItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {popularBeatmakers.length > 0 && (
                  <HeroCarousel
                    title="Популярні бітмейкери"
                    items={popularBeatmakers}
                    onItemClick={handleArtistClick}
                    type="artists"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {trendingItems.length > 0 && (
                  <HeroCarousel
                    title="Трендові біти"
                    items={trendingItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {newItems.length > 0 && (
                  <HeroCarousel
                    title="Нові біти"
                    items={newItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {freeItems.length > 0 && (
                  <HeroCarousel
                    title="Безкоштовні біти"
                    items={freeItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
              </>
            )}

            {/* Дизайн (Design) */}
            {selectedCategory === 'covers' && (
              <>
                {popularItems.length > 0 && (
                  <HeroCarousel
                    title="Популярний дизайн"
                    items={popularItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {popularDesigners.length > 0 && (
                  <HeroCarousel
                    title="Популярні дизайнери"
                    items={popularDesigners}
                    onItemClick={handleArtistClick}
                    type="artists"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {trendingItems.length > 0 && (
                  <HeroCarousel
                    title="Трендовий дизайн"
                    items={trendingItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {newItems.length > 0 && (
                  <HeroCarousel
                    title="Новий дизайн"
                    items={newItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
              </>
            )}

            {/* Фіти (Features) */}
            {selectedCategory === 'features' && (
              <>
                {popularArtists.length > 0 && (
                  <HeroCarousel
                    title="Популярні артисти"
                    items={popularArtists}
                    onItemClick={handleArtistClick}
                    type="artists"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {newItems.length > 0 && (
                  <HeroCarousel
                    title="Нові артисти"
                    items={newItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {trendingItems.length > 0 && (
                  <HeroCarousel
                    title="Трендові артисти"
                    items={trendingItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
              </>
            )}

            {/* Мікс/Мастерінг (Mix/Mastering) */}
            {selectedCategory === 'mixing' && (
              <>
                {popularItems.length > 0 && (
                  <HeroCarousel
                    title="Популярні послуги"
                    items={popularItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {popularSoundEngineers.length > 0 && (
                  <HeroCarousel
                    title="Популярні звукорежисери"
                    items={popularSoundEngineers}
                    onItemClick={handleArtistClick}
                    type="artists"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {newItems.length > 0 && (
                  <HeroCarousel
                    title="Нові послуги"
                    items={newItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {trendingItems.length > 0 && (
                  <HeroCarousel
                    title="Трендові мікси"
                    items={trendingItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
              </>
            )}

            {/* Кіти (Kits) */}
            {selectedCategory === 'kits' && (
              <>
                {popularItems.length > 0 && (
                  <HeroCarousel
                    title="Популярні кіти"
                    items={popularItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {popularProducers.length > 0 && (
                  <HeroCarousel
                    title="Популярні продюсери"
                    items={popularProducers}
                    onItemClick={handleArtistClick}
                    type="artists"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {newItems.length > 0 && (
                  <HeroCarousel
                    title="Нові кіти"
                    items={newItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {freeItems.length > 0 && (
                  <HeroCarousel
                    title="Безкоштовні кіти"
                    items={freeItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
              </>
            )}

            {/* Послуги (Services) - falls back to similar logic as mixing */}
            {selectedCategory === 'services' && (
              <>
                {popularItems.length > 0 && (
                  <HeroCarousel
                    title="Популярні послуги"
                    items={popularItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
                {newItems.length > 0 && (
                  <HeroCarousel
                    title="Нові послуги"
                    items={newItems}
                    onItemClick={handleArtistClick}
                    type="items"
                    onShowMore={() => {
                      haptic.light();
                      setShowSearchPage(true);
                    }}
                  />
                )}
              </>
            )}

            {/* Main Beat Feed */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-4 px-1">
                {selectedCategory === 'all' ? 'Усі біти' : translations.categories[selectedCategory as keyof typeof translations.categories]}
              </h2>
              
              {filteredItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Sparkles className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50 text-center">
                    {searchQuery ? 'Нічого не знайдено' : 'Поки що тут порожньо'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {filteredItems
                    .filter(item => item.category === 'beat' || selectedCategory !== 'all')
                    .map((item) => (
                      <PortfolioMiniCard
                        key={item.id}
                        item={item}
                        onClick={() => setSelectedItem(item)}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Home - Swipes View */}
        {activeBottomTab === 'home' && activeTopTab === 'Swipes' && !showSearchPage && (
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-250px)] relative">
            {currentSwipeItem ? (
              <>
                <div className="relative w-full max-w-sm h-[480px]">
                  <SwipeCardProfile
                    key={`profile-${currentSwipeItem.id}`}
                    item={currentSwipeItem}
                    onSwipe={handleSwipe}
                    onInfo={() => {
                      if (currentSwipeItem.candidate) {
                        setSelectedArtistProfile(candidateToParsedProfile(currentSwipeItem.candidate));
                      }
                    }}
                  />
                </div>

                <div className="mt-8 flex items-center gap-2 px-4 py-2 rounded-2xl glass border border-white/10">
                  <Sparkles className="w-4 h-4 text-splyt-green glow-green" />
                  <div>
                    <p className="text-white/70 text-sm">
                      {translations.swipes.swipeToDiscover}
                    </p>
                    {swipesRemaining !== null && (
                      <p className="text-white/40 text-xs mt-1">
                        {swipesRemaining < 0
                          ? 'Безлімітні свайпи активні'
                          : `Залишилось ${swipesRemaining} свайпів на сьогодні`}
                      </p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center">
                <Sparkles className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">
                  {swipesRemaining === 0
                    ? 'Ви використали всі свайпи на сьогодні. Поверніться завтра!'
                    : translations.swipes.noMoreItems}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Saved Tab */}
        {activeBottomTab === 'saved' && (
          <div>
            <h2 className="text-2xl font-bold mb-6 text-white">
              {translations.nav.saved} ({savedItems.length})
            </h2>
            {savedItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 bg-white/5">
                  <Sparkles className="w-10 h-10 text-white/30" />
                </div>
                <p className="text-white/50 text-center">
                  {translations.swipes.likeToSave}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 pb-4">
                {savedItems.map((item) => (
                  <PortfolioMiniCard
                    key={item.id}
                    item={item}
                    onClick={() => setSelectedItem(item)}
                  />
                ))}
              </div>
            )}

            <div className="mt-8">
              <h3 className="text-xl font-bold mb-4 text-white">
                Мої покупки
              </h3>
              <PurchasesList purchases={purchases} loading={purchasesLoading} />
            </div>
          </div>
        )}

        {/* Chats Tab */}
        {activeBottomTab === 'chats' && !selectedChat && (
          <ChatsView
            onBoostClick={() => {
              haptic.medium();
              setShowBoostPromo(true);
            }}
            onChatClick={(match) => {
              haptic.medium();
              setSelectedChat({ id: match.id, name: match.name, photo: match.photo });
            }}
          />
        )}

        {/* Chat Conversation View */}
        {activeBottomTab === 'chats' && selectedChat && (
          <ChatConversationView
            userId={selectedChat.id}
            userName={selectedChat.name}
            userPhoto={selectedChat.photo}
            onBack={() => {
              haptic.light();
              setSelectedChat(null);
            }}
          />
        )}

        {/* Profile Tab */}
        {activeBottomTab === 'profile' && (
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 80px)' }}>
            {userProfile ? (
              <ProfileView
                profile={userProfile}
                referralLink={referralLink}
                onEdit={() => {
                  haptic.medium();
                  setShowEditProfile(true);
                }}
                onPrivacy={() => {
                  haptic.medium();
                  setShowPrivacySettings(true);
                }}
                onVerification={() => {
                  haptic.medium();
                  setShowVerification(true);
                }}
                onDeleteProfile={async () => {
                  if (confirm('Ви впевнені, що хочете видалити свій профіль? Цю дію неможливо скасувати!')) {
                    try {
                      await api.profile.delete();
                      setUserProfile(null);
                      setShowOnboarding(true);
                      haptic.success();
                    } catch (e) {
                      console.error('Failed to delete profile:', e);
                      alert('Помилка при видаленні профілю');
                      haptic.error();
                    }
                  }
                }}
                onWrite={handleWrite}
              />
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
                <div className="w-24 h-24 rounded-full mb-4 bg-gradient-to-br from-[#00FF88] to-[#8B5CF6]" />
                <h2 className="text-xl font-bold text-white mb-2">Профіль не знайдено</h2>
                <p className="text-white/50 mb-6 text-center">Створіть профіль, щоб продовжити</p>
                <button
                  onClick={() => {
                    haptic.medium();
                    setShowOnboarding(true);
                  }}
                  className="px-6 py-3 rounded-2xl border backdrop-blur-xl transition-all hover:border-[#00FF88]/50"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.2) 0%, rgba(0, 255, 136, 0.1) 100%)',
                    borderColor: 'rgba(0, 255, 136, 0.5)',
                    color: '#00FF88',
                  }}
                >
                  Створити профіль
                </button>
              </div>
            )}
          </div>
        )}

        {/* Add Tab - Upload view is shown directly when tab is active */}
      </div>

      {/* Developer Mode banner */}
      {isDevModeEnabled() && (
        <div className="fixed top-2 right-2 z-50 px-3 py-1 rounded-full glass border border-splyt-green/40 bg-splyt-green/10 text-splyt-green text-xs">
          Developer Mode
        </div>
      )}

      {/* Bottom Navigation */}
      {!selectedChat && !showUploadView && (
        <BottomNav activeTab={activeBottomTab} onTabChange={handleBottomTabChange} />
      )}

      {/* Item Post View (Fullscreen like profile posts) */}
      {selectedItem && !showItemInfo && (
        <PortfolioSwipeCardModal
          item={{
            type: selectedItem.category as any,
            value: selectedItem.preview_url || selectedItem.media_url || '',
            title: selectedItem.title,
            description: selectedItem.description,
            price_uah: selectedItem.price_uah,
            cover_url: selectedItem.cover_url,
            preview_url: selectedItem.preview_url,
            category: selectedItem.category,
            item_id: selectedItem.id,
            source: 'marketplace' as const,
            created_at: selectedItem.created_at,
            link: selectedItem.preview_url || selectedItem.media_url,
          }}
          owner={{
            id: selectedItem.creator_id,
            name: selectedItem.creator?.name,
            username: selectedItem.creator?.username,
            avatar: selectedItem.creator?.avatar,
          }}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {/* Item Detail Modal (Info/Description view) */}
      {selectedItem && showItemInfo && (
        <ItemDetailModal
          item={selectedItem}
          onClose={() => {
            setShowItemInfo(false);
            setSelectedItem(null);
          }}
          onLike={() => {
            toggleLike(selectedItem.id);
          }}
          isLiked={likedItems.has(selectedItem.id)}
          onMessageClick={(userId, username) => {
            haptic.medium();
            setShowItemInfo(false);
            setSelectedItem(null);
            setActiveBottomTab('chats');
            setSelectedChat({
              id: String(userId),
              name: username || 'Creator',
            });
          }}
        />
      )}

      {/* User Profile Modal */}
      {selectedArtistProfile && (
        <UserProfileModal
          profile={selectedArtistProfile}
          onClose={() => setSelectedArtistProfile(null)}
          onMessageClick={(userId, username) => {
            haptic.medium();
            setSelectedArtistProfile(null);
            setActiveBottomTab('chats');
            setSelectedChat({
              id: String(userId),
              name: username || 'User',
            });
          }}
            onWrite={handleWrite}
        />
      )}

      {/* Filter Dropdown */}
      <FilterDropdown
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onApply={handleFilterApply}
        category={selectedCategory}
      />

      {/* Boost Promo View */}
      {showBoostPromo && (
        <BoostPromoView
          userName={userProfile?.name || userProfile?.username}
          onClose={() => {
            haptic.light();
            setShowBoostPromo(false);
          }}
          onGetBoost={() => {
            haptic.medium();
            setShowBoostPromo(false);
            // TODO: Implement boost purchase flow
            console.log('Navigate to boost purchase');
          }}
          onViewPlans={() => {
            haptic.medium();
            setShowBoostPromo(false);
            // TODO: Implement view plans flow
            console.log('Navigate to view plans');
          }}
        />
      )}

      {/* Upload Work View */}
      {showUploadView && (
        <UploadWorkView
          onBack={() => {
            haptic.light();
            setShowUploadView(false);
            // Return to home tab when closing upload view
            setActiveBottomTab('home');
          }}
          onPublish={async (data) => {
            haptic.medium();
            try {
              const apiPayload: CreateWorkData = {
                title: data.title,
                description: data.description,
                tags: data.tags,
                price: data.price,
                category: data.category,
                bpm: data.bpm,
                key: data.key,
                // New upload model fields
                deliveryFormats: data.deliveryFormats,
                previewFile: data.previewFile?.file ?? undefined,
                coverFile: data.coverFile?.file ?? undefined,
                deliverableFiles: data.deliverableFiles.map((item) => item.file),
              };

              await api.catalog.create(apiPayload);
              
              // For now, show success message
              tg?.showAlert('Роботу успішно опубліковано!');
              
              // Reset form and go back to home
              setShowUploadView(false);
              setActiveBottomTab('home');
            } catch (error) {
              console.error('Failed to publish work:', error);
              tg?.showAlert('Не вдалося опублікувати роботу. Спробуйте ще раз.');
              haptic.error();
            }
          }}
        />
      )}
    </div>
  );
}

